import {BehaviorSubject, Observable, of} from 'rxjs';

import {AlertService} from '../service/alert.service';

/**
 * Shared load-error policy for dashboard-style read models. Marks the supplied error subject so the template can
 * render a "Data unavailable" placeholder, and surfaces a toast for non-503 failures. 503 ("Service unavailable")
 * gets only the inline placeholder — no toast — because the placeholder already tells the merchant the data
 * couldn't be loaded.
 *
 * @param err the caught load failure, checked for its HTTP status
 * @param errorSubject flipped to {@code true} so the owning template shows its placeholder; omit when no flag exists
 * @param toastMessage the message surfaced for non-503 failures
 * @param fallback the value emitted in place of the failed load
 * @param alertService the toast sink
 * @returns an observable emitting the fallback value
 */
export function handleLoadError<T>(
    err: any,
    errorSubject: BehaviorSubject<boolean> | undefined,
    toastMessage: string,
    fallback: T,
    alertService: AlertService): Observable<T>
{
    errorSubject?.next(true);
    if (!rendersPlaceholderOnly(err))
    {
        alertService.error(toastMessage);
    }
    return of(fallback);
}

/**
 * Whether a failed load already explains itself through its inline placeholder, so a toast would only repeat it.
 * Callers that render their own placeholder share this rule rather than re-checking the status.
 *
 * @param err the caught load failure
 * @returns true when the placeholder alone is the right signal
 */
export function rendersPlaceholderOnly(err: any): boolean
{
    return err?.status === 503;
}
